import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kMatterArtifactApplyTemplateApiPath } from '@/agent-v2/artifact-templates/constants'
import { MatterArtifactAppliedTemplateReadSchema, type MatterArtifactAppliedTemplateRead } from '@/artifact-templates/schemas'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { z } from 'zod'

export type ApplyTemplateToMatterArtifactResponse = {
  // The persisted applied record for this version, stored locally so a later download/save can
  // serve the output (via a signed URL) without re-applying.
  appliedTemplate: MatterArtifactAppliedTemplateRead
  // Short-lived signed URL for the freshly rendered docx.
  downloadUrl: string
}

const ApplyTemplateToMatterArtifactApiResponseSchema = z.object({
  applied_template: MatterArtifactAppliedTemplateReadSchema,
  download_url: z.string().min(1),
  expires_at: z.string(),
})

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}

/**
 * Apply a template to a Matter artifact version. The backend renders the version's markdown with
 * the template, persists the applied record for that version, and returns it plus a signed
 * download URL for the rendered docx.
 */
export async function postApplyTemplateToMatterArtifact(body: {
  matterArtifactVersionId: string
  templateUserFileId: string
}): Promise<ApplyTemplateToMatterArtifactResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactApplyTemplateApiPath}`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
      body: JSON.stringify({
        matter_artifact_version_id: body.matterArtifactVersionId,
        template_user_file_id: body.templateUserFileId,
      }),
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Apply Matter artifact template failed - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: body,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, `Failed to apply template: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Apply Matter artifact template response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: body,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid apply template response JSON')
    }

    const parsed = ApplyTemplateToMatterArtifactApiResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Apply Matter artifact template response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            requestBody: body,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid apply template response shape', parsed.error.issues)
    }

    return { appliedTemplate: parsed.data.applied_template, downloadUrl: parsed.data.download_url }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
